import {
  type JsonValue,
  type MapBodyExecutionContract,
  type MapControlResult,
  type MapExecutionConfig,
  type MapProgress,
  type NodeParam,
  type NodeStruct,
} from '@/types/node-types';
import type { NodeStatusEvent } from '@/types/workflow-types';

export const MAP_NODE_ID = 'map_node';
export const MAP_CAPABILITY = 'workflow.map.v1';
export const MAP_REQUIRED_CAPABILITIES = [
  MAP_CAPABILITY,
  'workflow.map.activity_dispatch.v1',
  'workflow.map.persistence.v1',
  'workflow.map.ordered_results.v1',
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isBodySlot = (value: unknown): boolean =>
  isRecord(value) &&
  (value.kind === 'input' || value.kind === 'param') &&
  typeof value.target_id === 'string' &&
  value.target_id.length > 0;

const bodySlotsEqual = (
  left: { kind: string; target_id: string } | null,
  right: { kind: string; target_id: string } | null,
): boolean =>
  left === null || right === null
    ? left === right
    : left.kind === right.kind && left.target_id === right.target_id;

const isMapBody = (value: unknown): boolean =>
  isRecord(value) &&
  value.kind === 'call' &&
  [
    'semantic_child_id',
    'catalog_node_id',
    'node_version',
    'node_contract_version',
    'capability_identity',
    'catalog_contract_digest',
  ].every((key) => typeof value[key] === 'string') &&
  isBodySlot(value.item_slot) &&
  (value.index_slot === null || isBodySlot(value.index_slot)) &&
  isRecord(value.static_params) &&
  isRecord(value.node_template);

const hasAllMapCapabilities = (capabilities: unknown): boolean => {
  if (!Array.isArray(capabilities)) return false;
  const values = new Set(
    capabilities.filter((value): value is string => typeof value === 'string'),
  );
  return MAP_REQUIRED_CAPABILITIES.every((capability) =>
    values.has(capability),
  );
};

export const isMapNode = (struct: NodeStruct | undefined): boolean =>
  struct?.node_id === MAP_NODE_ID;

export const isExecutableMapCarrier = (struct: NodeStruct): boolean =>
  struct.node_id === MAP_NODE_ID &&
  struct.capability_identity === MAP_CAPABILITY &&
  struct.contract_version === '1.0' &&
  struct.runtime_special_case === 'temporal_map_v1' &&
  hasAllMapCapabilities(struct.required_capabilities);

export const isMapBodyEligible = (struct: NodeStruct): boolean => {
  const contract = struct.map_body_contract;
  return (
    struct.node_id !== MAP_NODE_ID &&
    struct.map_body_supported === true &&
    struct.execution_class === 'deterministic' &&
    (struct.effect_class === 'none' || struct.effect_class === 'read') &&
    struct.idempotent === true &&
    Boolean(struct.contract_version) &&
    Boolean(struct.capability_identity) &&
    Boolean(struct.catalog_contract_digest) &&
    hasAllMapCapabilities(struct.map_required_runtime_capabilities) &&
    contract?.version === '1.0' &&
    contract.supports_files === false &&
    contract.supports_handles === false &&
    contract.supports_clarification === false &&
    contract.item_slots.length > 0
  );
};

export const sanitizeMapCatalog = (catalog: NodeStruct[]): NodeStruct[] =>
  catalog
    .filter(
      (node) => node.node_id !== MAP_NODE_ID || isExecutableMapCarrier(node),
    )
    .map((node) => {
      if (node.node_id === MAP_NODE_ID || !node.map_body_supported) return node;
      return isMapBodyEligible(node)
        ? node
        : { ...node, map_body_supported: false };
    });

const defaultMapConfig = (
  instanceId: string,
  carrier: NodeStruct,
): MapExecutionConfig => ({
  version: '1.0',
  semantic_id: `map:${instanceId}`,
  item_name: 'item',
  index_name: 'index',
  max_items: Math.min(64, carrier.map_runtime_limits?.max_items ?? 64),
  max_concurrency: Math.min(
    4,
    carrier.map_runtime_limits?.max_concurrency ?? 4,
  ),
  failure_mode: 'all_settled',
  preserve_input_order: true,
  item_timeout_seconds: 300,
  item_max_attempts: 1,
  idempotency_mode: 'required',
  cache_mode: 'disabled',
  body: null,
});

export const getMapConfig = (
  struct: NodeStruct | undefined,
): MapExecutionConfig | null => {
  if (!struct || !isMapNode(struct)) return null;
  const value = struct.params.find(
    (param) => param.field_id === 'map_config',
  )?.value;
  if (!isRecord(value) || value.version !== '1.0') return null;
  if (
    typeof value.semantic_id !== 'string' ||
    typeof value.item_name !== 'string' ||
    typeof value.index_name !== 'string' ||
    typeof value.max_items !== 'number' ||
    typeof value.max_concurrency !== 'number' ||
    (value.failure_mode !== 'fail_fast' &&
      value.failure_mode !== 'all_settled') ||
    value.preserve_input_order !== true ||
    typeof value.item_timeout_seconds !== 'number' ||
    typeof value.item_max_attempts !== 'number' ||
    value.idempotency_mode !== 'required' ||
    value.cache_mode !== 'disabled' ||
    (value.body !== null && !isMapBody(value.body))
  ) {
    return null;
  }
  return value as unknown as MapExecutionConfig;
};

export const setMapConfig = (
  struct: NodeStruct,
  config: MapExecutionConfig,
): NodeStruct => ({
  ...struct,
  params: struct.params.map((param) =>
    param.field_id === 'map_config'
      ? { ...param, value: config as unknown as JsonValue }
      : param,
  ),
});

export const normalizeMapStructForInstance = (
  struct: NodeStruct,
  instanceId: string,
): NodeStruct => {
  if (!isMapNode(struct)) return struct;
  const current = getMapConfig(struct) ?? defaultMapConfig(instanceId, struct);
  const body = current.body
    ? {
        ...current.body,
        semantic_child_id: `map-body:${instanceId}`,
        node_template: {
          ...current.body.node_template,
          react_flow_generated_id: `map-body:${instanceId}`,
        },
      }
    : null;
  return setMapConfig(struct, {
    ...current,
    version: '1.0',
    semantic_id: `map:${instanceId}`,
    preserve_input_order: true,
    idempotency_mode: 'required',
    cache_mode: 'disabled',
    body,
    runtime_limits: null,
  });
};

const paramJsonValue = (param: NodeParam): JsonValue => {
  const value = param.value ?? param.default ?? null;
  return JSON.parse(JSON.stringify(value)) as JsonValue;
};

export type AttachMapBodyResult =
  | { ok: true; config: MapExecutionConfig }
  | { ok: false; reason: string };

export const attachMapBody = (
  config: MapExecutionConfig,
  leaf: NodeStruct,
): AttachMapBodyResult => {
  if (config.body) {
    return { ok: false, reason: 'Map already has its single body call.' };
  }
  if (!isMapBodyEligible(leaf) || !leaf.map_body_contract) {
    return {
      ok: false,
      reason: 'This catalog node is not declared safe for a Map body.',
    };
  }

  const staticParams: Record<string, JsonValue> = {};
  for (const fieldId of leaf.map_body_contract.static_params) {
    const param = leaf.params.find(
      (candidate) => candidate.field_id === fieldId,
    );
    if (!param) {
      return {
        ok: false,
        reason: `Body contract is missing static parameter ${fieldId}.`,
      };
    }
    staticParams[fieldId] = paramJsonValue(param);
  }

  const semanticChildId = `map-body:${config.semantic_id.replace(/^map:/, '')}`;
  const body: MapBodyExecutionContract = {
    kind: 'call',
    semantic_child_id: semanticChildId,
    catalog_node_id: leaf.node_id,
    node_version: leaf.version,
    node_contract_version: leaf.contract_version!,
    capability_identity: leaf.capability_identity!,
    catalog_contract_digest: leaf.catalog_contract_digest!,
    item_slot: { ...leaf.map_body_contract.item_slots[0] },
    index_slot: leaf.map_body_contract.index_slots[0]
      ? { ...leaf.map_body_contract.index_slots[0] }
      : null,
    static_params: staticParams,
    node_template: {
      ...(JSON.parse(JSON.stringify(leaf)) as Record<string, JsonValue>),
      react_flow_generated_id: semanticChildId,
    },
  };
  return { ok: true, config: { ...config, body, runtime_limits: null } };
};

export const detachMapBody = (
  config: MapExecutionConfig,
): MapExecutionConfig => ({ ...config, body: null, runtime_limits: null });

export const validateMapConfig = (
  config: MapExecutionConfig | null,
  catalog?: NodeStruct[],
): string[] => {
  if (!config) return ['Map execution metadata is missing or malformed.'];
  const errors: string[] = [];
  if (!config.body) {
    errors.push('Choose exactly one supported body call.');
    return errors;
  }
  if (!config.item_name || !config.index_name) {
    errors.push('Item and index names are required.');
  }
  if (config.item_name === config.index_name) {
    errors.push('Item and index names must be different.');
  }
  const carrier = catalog?.find(
    (candidate) => candidate.node_id === MAP_NODE_ID,
  );
  const maxItems =
    carrier?.map_runtime_limits?.max_items ??
    config.runtime_limits?.max_items ??
    256;
  const maxConcurrency =
    carrier?.map_runtime_limits?.max_concurrency ??
    config.runtime_limits?.max_concurrency ??
    256;
  if (config.max_items < 1 || config.max_items > maxItems) {
    errors.push(`Max items must be between 1 and ${maxItems}.`);
  }
  if (config.max_concurrency < 1 || config.max_concurrency > maxConcurrency) {
    errors.push(`Max concurrency must be between 1 and ${maxConcurrency}.`);
  }
  if (config.item_max_attempts < 1 || config.item_max_attempts > 5) {
    errors.push('Body attempts must be between 1 and 5.');
  }
  if (config.item_timeout_seconds < 1 || config.item_timeout_seconds > 3_600) {
    errors.push('Per-item timeout must be between 1 and 3600 seconds.');
  }

  const current = catalog?.find(
    (candidate) =>
      candidate.node_id === config.body?.catalog_node_id &&
      candidate.version === config.body?.node_version,
  );
  if (catalog) {
    if (!current || !isMapBodyEligible(current)) {
      errors.push('The pinned body call is unavailable or no longer Map-safe.');
    } else if (
      current.contract_version !== config.body.node_contract_version ||
      current.capability_identity !== config.body.capability_identity ||
      current.catalog_contract_digest !== config.body.catalog_contract_digest
    ) {
      errors.push(
        'The pinned body call is stale; remove it and choose it again.',
      );
    } else if (
      current.map_body_contract &&
      (!current.map_body_contract.item_slots.some((slot) =>
        bodySlotsEqual(config.body!.item_slot, slot),
      ) ||
        (config.body.index_slot !== null &&
          !current.map_body_contract.index_slots.some((slot) =>
            bodySlotsEqual(config.body!.index_slot, slot),
          )) ||
        new Set(Object.keys(config.body.static_params)).size !==
          new Set(current.map_body_contract.static_params).size ||
        current.map_body_contract.static_params.some(
          (fieldId) => !(fieldId in config.body!.static_params),
        ))
    ) {
      errors.push('The Map body slots or static parameters are malformed.');
    }
  }
  return errors;
};

export const parseMapControlResult = (
  value: unknown,
): MapControlResult | null => {
  let candidate = value;
  if (isRecord(candidate) && isRecord(candidate.data))
    candidate = candidate.data;
  if (!isRecord(candidate)) return null;
  if (
    !Array.isArray(candidate.results) ||
    !isRecord(candidate.counts) ||
    typeof candidate.digest !== 'string' ||
    !isRecord(candidate.provenance)
  ) {
    return null;
  }
  return candidate as unknown as MapControlResult;
};

export const latestMapProgress = (
  events: NodeStatusEvent[] | undefined,
): MapProgress | null => {
  if (!events) return null;
  for (let index = events.length - 1; index >= 0; index--) {
    const data = (events[index] as unknown as { data?: unknown }).data;
    if (!isRecord(data) || !isRecord(data.map_control)) continue;
    const progress = data.map_control;
    if (
      ['input', 'admitted', 'completed', 'failed'].every(
        (key) => typeof progress[key] === 'number',
      )
    ) {
      return progress as unknown as MapProgress;
    }
  }
  return null;
};
